import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator";
import filterFactory from "react-bootstrap-table2-filter";
import clsx from "clsx";
import { Button } from "react-bootstrap";
import LoadingOverlay from "./LoadingOverlay";
import styles from "./DataTable.scss";

const propTypes = {
  /**
   * Set keyField for the datatable
   */
  keyField: PropTypes.string.isRequired,
  /**
   * Set row selected by ids
   */
  selected: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  /**
   * Callback for when the single row get selected
   */
  onSelect: PropTypes.func.isRequired,
  /**
   * Callback for when all rows get selected
   */
  onSelectAll: PropTypes.func.isRequired,
  /**
   * Display number/size of items per page
   */
  sizePerPage: PropTypes.number,
  /**
   * Display option for setting sizePerPage
   */
  sizeOptions: PropTypes.arrayOf(PropTypes.number),
  /**
   * Callback for when all rows get selected
   */
  onPageSizeChange: PropTypes.func.isRequired,
  /**
   * Callback for when all rows get selected
   */
  page: PropTypes.number.isRequired,
  /**
   * Callback when page change
   */
  onPageChanges: PropTypes.func.isRequired,
  /**
   * Custom sort for datatable
   */
  sort: PropTypes.shape().isRequired,
  /**
   * Enable selection row for datatable
   */
  enableSelection: PropTypes.bool,
  /**
   * data for datatable
   */
  data: PropTypes.arrayOf(PropTypes.shape()),
  /**
   * Columns for datatable
   */
  columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  /**
   * totalSize of items for datatable
   */
  totalSize: PropTypes.number.isRequired,
  /**
   * totalSize of items for datatable
   */
  disabledByKey: PropTypes.string,
  /**
   * prop to reset table
   */
  reset: PropTypes.bool,
  /**
   * Indicates if table is loading
   */
  isTableLoading: PropTypes.bool,
  /**
   * Indicates table row events
   */
  tableRowEvents: PropTypes.shape({
    onClick: PropTypes.func,
    onDoubleClick: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onContextMenu: PropTypes.func,
  }),
  /**
   * Indicates table row classes
   */
  customRowClass: PropTypes.string,
};

const defaultProps = {
  enableSelection: false,
  sizePerPage: 10,
  selected: [],
  sizeOptions: [10, 25, 50, 100],
  data: [],
  disabledByKey: "",
  reset: null,
  isTableLoading: false,
  tableRowEvents: () => {},
  customRowClass: null,
};

/**
 * List down data with checkboxes
 * Based on https://www.npmjs.com/package/react-bootstrap-table-next/.
 */
function DataTable(props) {
  const {
    keyField,
    data,
    columns,
    sizePerPage,
    totalSize,
    enableSelection,
    selected,
    onSelect,
    onSelectAll,
    sizeOptions,
    onPageSizeChange,
    page,
    disabledByKey,
    onPageChanges,
    sort,
    reset,
    isTableLoading,
    tableRowEvents,
    customRowClass,
  } = props;

  const [show, setShow] = useState(true);

  useEffect(() => {
    if (reset !== null) {
      setShow(false);
      setTimeout(() => {
        setShow(true);
      }, 0);
    }
  }, [reset]);

  // eslint-disable-next-line no-shadow
  const onPageChange = (page, sizePerPage) => onPageChanges(page, sizePerPage);

  const dataTableOptions = {
    custom: true,
    paginationSize: 4,
    pageStartIndex: 1,
    nextPageTitle: "First page",
    prePageTitle: "Pre page",
    firstPageTitle: "Next page",
    lastPageTitle: "Last page",
    firstPageText: "«",
    prePageText: "‹",
    nextPageText: "›",
    lastPageText: "»",
    hidePageListOnlyOnePage: true,
    alwaysShowAllBtns: true,
    showTotal: true,
    sizePerPage,
    totalSize,
    page,
    onPageChange,
  };

  const handleOnSelect = (row, isSelect) => onSelect(row, isSelect);

  const handleOnSelectAll = (isSelect, rows) => onSelectAll(isSelect, rows);

  const handleSizePerPage = ({ onSizePerPageChange }, newSizePerPage) => {
    onSizePerPageChange(newSizePerPage, 1);
    onPageSizeChange(newSizePerPage);
  };

  const rowClasses = (row) => {
    let classes = null;
    if (disabledByKey && row[disabledByKey] === false) {
      classes = "Uic-DataTable-blur_row";
    }
    return classes;
  };

  const renderSizeButtons = (paginationProps) => {
    let once = true;
    return (
      <div className="btn-group" role="group">
        {sizeOptions?.map((size) => {
          if (size < paginationProps.dataSize || once === true) {
            if (!(size < paginationProps.dataSize)) {
              once = false;
            }
            return (
              <Button
                key={size}
                onClick={() => handleSizePerPage(paginationProps, size)}
                variant={sizePerPage === size ? "primary" : "light"}
              >
                {size}
              </Button>
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <div className={clsx(styles.base)}>
      <PaginationProvider pagination={paginationFactory(dataTableOptions)}>
        {({ paginationProps, paginationTableProps }) => (
          <div>
            {show && (
              <LoadingOverlay loading={isTableLoading}>
                <BootstrapTable
                  keyField={keyField}
                  data={data}
                  columns={columns}
                  rowClasses={(row) => clsx(rowClasses(row), customRowClass)}
                  sort={sort}
                  selectRow={
                    enableSelection
                      ? {
                          mode: "checkbox",
                          selected,
                          onSelect: handleOnSelect,
                          onSelectAll: handleOnSelectAll,
                        }
                      : { mode: "checkbox", hideSelectColumn: true }
                  }
                  bordered={false}
                  filter={filterFactory()}
                  noDataIndication={
                    <div className={styles.no_data}>
                      <FormattedMessage id="UIC.GENERAL.NO_DATA" />
                    </div>
                  }
                  hover={false}
                  rowEvents={tableRowEvents}
                  {...paginationTableProps}
                />
              </LoadingOverlay>
            )}
            <div className="row">
              <div className="col">{renderSizeButtons(paginationProps)}</div>
              <div className="col">
                <PaginationListStandalone {...paginationProps} />
              </div>
            </div>
          </div>
        )}
      </PaginationProvider>
    </div>
  );
}

DataTable.propTypes = propTypes;
DataTable.defaultProps = defaultProps;

export default DataTable;
